import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router";
import { paths } from "src/paths";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  signOut,
  deleteUser,
} from "firebase/auth";
import { firebaseApp } from "src/libs/firebase";
import { Issuer } from "src/utils/auth";
import { getUserInfo } from "src/pages/dashboard/account";
import { resolveSessionPhotoDisplayUrl } from "src/utils/getBlobUrl";
import {
  API_AUTH_SERVICE_BACKEND,
  API_SERVICE_BACKEND,
  API_SERVICE_BACKEND_2,
  isLocalOrStagingEnv,
} from "src/config";
import axios from "axios";
import {
  clearLocalStorage,
  clearSessionStorage,
  getSessionStorage,
  setSessionStorage,
} from "src/utils/storage";
import { generateHMAC } from "src/pages/auth-demo/login/modern";
import { useSearchParams } from "src/hooks/use-search-params";
import { toast } from "react-hot-toast";
import { INTEGRATION_TYPE } from "src/helpers/constants";
import Cookies from "js-cookie";

const auth = getAuth(firebaseApp);

var ActionType;
(function (ActionType) {
  ActionType["AUTH_STATE_CHANGED"] = "AUTH_STATE_CHANGED";
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === "AUTH_STATE_CHANGED") {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }

  return state;
};

let loginInProgress = false;

export const AuthContext = createContext({
  ...initialState,
  issuer: Issuer.Firebase,
  createUserWithEmailAndPassword: () => Promise.resolve(),
  sendEmailVerification: () => Promise.resolve(),
  signInWithEmailAndPassword: () => Promise.resolve(),
  signInWithGoogle: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  sendPasswordResetEmail: () => Promise.resolve(),
  deleteUser: () => Promise.resolve(),
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const { pathname, state: locState } = useLocation();

  setSessionStorage("currentUrl", pathname);

  //console.log("locState", locState);

  const searchParams = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const authToken =
    searchParams.get("token") || getSessionStorage("jwtLoginToken");
  /**
   * Single source for /token API – used by:
   * - Magic link login
   * - Sign in with email/password
   * - Sign in with Google
   * Any change to request body, headers, or cookie storage should be done here only.
   */
  const fetchAndStoreAuthToken = useCallback(async (firebaseToken) => {
    try {
      if (!firebaseToken) {
        throw new Error("Firebase token is required");
      }

      const requestBody = { token: firebaseToken };

      const jwtResponse = await axios.post(
        `${API_AUTH_SERVICE_BACKEND}/token`,
        requestBody,
      );

      const jwtToken = jwtResponse?.data?.token;

      if (!jwtToken) {
        throw new Error("No token received from authentication service");
      }

      // On localhost, store token in sessionStorage for Authorization header injection
      if (isLocalOrStagingEnv()) {
        window.sessionStorage.setItem('b2b_at_local',jwtToken);
      }

      // Backend sets httpOnly cookie via Set-Cookie header automatically

      return jwtResponse.data;
    } catch (err) {
      console.error("Backend token API failed:", err);
      throw err;
    }
  }, []);

  const _signOut = useCallback(async () => {
    await signOut(auth);
    clearLocalStorage();
    clearSessionStorage();
    dispatch({
      type: ActionType.AUTH_STATE_CHANGED,
      payload: {
        isAuthenticated: false,
        user: null,
      },
    });
  }, []);

  const _forgotPassword = useCallback(async (email) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  const magicLinkLogin = async (authToken) => {
    // clearSessionStorage();

    try {
      //console.log("auth: signed out!");

      // Decode JWT token locally instead of calling external API
      let authData;
      try {
        // Split the JWT token (format: header.payload.signature)
        const tokenParts = authToken.split('.');
        if (tokenParts.length !== 3) {
          throw new Error("Invalid token format");
        }
        
        // Decode the payload (second part)
        const payload = JSON.parse(atob(tokenParts[1]));
        
        // Extract user data from token payload
        authData = {
          email: payload.email,
          userEmail: payload.userEmail || "",
          adminUserEmail: payload.adminUserEmail || "",
          teamMemberEmail: payload.teamMemberEmail || "",
          role: payload.role || "",
        };
        
        // console.log("✅ JWT token decoded successfully:", authData);
      } catch (decodeError) {
        console.error("❌ Failed to decode JWT token:", decodeError);
        toast.error("Invalid authentication token");
        clearSessionStorage();
        setTimeout(() => {
          window.location.href = `${pathname}`;
        }, 2000);
        return;
      }

      // 0. Clear any stale session belonging to a different user BEFORE
      // fetching/storing the new token below - otherwise clearSessionStorage()
      // wipes out the b2b_at_local token we just stored, causing a 401
      // "header missing" on the very next authenticated request (localhost only,
      // since production relies on the httpOnly cookie instead of b2b_at_local).
      const verifyEmail = getSessionStorage("userEmail");
      if (authData.email !== verifyEmail) {
        clearSessionStorage();
      }

      // 1. First: Token API call - exchange magic link JWT for b2b_at (common flow for all login methods)
      // Clear old b2b_at to avoid 401 - backend may reject request if old token sent in header
      Cookies.remove("b2b_at");
      try {
        await fetchAndStoreAuthToken(authToken);
      } catch (err) {
        toast.error("Authentication failed. Redirecting to login...");
        clearSessionStorage();
        setTimeout(() => {
          window.location.href = "/auth-main/login/v2";
        }, 2000);
        return;
      }

      let requestStatus = searchParams?.get("status") || null;

      if (authData.userEmail && requestStatus == "accept") {
        await axios
          .put(`${API_SERVICE_BACKEND}/invitation/update`, {
            receiver_mail: authData.userEmail,
            sender_mail: authData.email,
            status: requestStatus,
          })
          .then((res) => res.data)
          .catch((err) => {
            //console.log(err);
          });
      }
      const userInfo = await getUserInfo(authData.email);

      if (!userInfo) {
        clearSessionStorage();
        setSessionStorage("magic_link_status", "in-active");
        window.location.href = `${pathname}`;
      } else {
        // Magic link login: Send magic link token to backend
        // Backend will detect it's a magic link token (not Firebase token) and handle accordingly
        try {
          const requestBody = { 
            token: authToken  // Send magic link JWT token
          };
          
          const jwtResponse = await axios.post(
            `${API_AUTH_SERVICE_BACKEND}/token`,
            requestBody,
          );
          
          const token = jwtResponse?.data?.token;
          if (!token) {
            throw new Error("No token received from auth service");
          }

          // On localhost, store token in sessionStorage for Authorization header injection
          if (isLocalOrStagingEnv()) {
            window.sessionStorage.setItem('b2b_at_local', token);
          }

          // Backend sets httpOnly cookie via Set-Cookie header automatically
          // console.log("✅ Magic link authentication successful");
        } catch (err) {
          // console.error("❌ Magic link authentication failed:", err);
          toast.error("Authentication failed. Redirecting to login...");
          clearSessionStorage();
          setTimeout(() => {
            window.location.href = "/auth-main/login/v2";
          }, 2000);
          return; // Stop execution
        }
        
        setSessionStorage("jwtLoginToken", authToken);
        // //console.log({ authData, userInfo });
        const user = {
          email: userInfo.email,
          displayName: userInfo.name,
          photoURL: userInfo.profile_image,
          firstName: userInfo.name.split(" ")[0],
          lastName: userInfo.name.split(" ")[1],
        };
        setSessionStorage("fullName", userInfo.name);
        setSessionStorage("userEmail", userInfo.email);
        setSessionStorage("ownerEmail", authData.userEmail || "");
        setSessionStorage("adminUserEmail", authData.adminUserEmail || "");
        setSessionStorage("teamMemberEmail", authData?.teamMemberEmail || "");
        if (authData.userEmail !== "") {
          const isOwnWorkspace = Boolean(authData.userEmail && authData.userEmail === authData.email);
          setSessionStorage("isOwnWorkspace", isOwnWorkspace);
        }
        if (authData?.role) {
          setSessionStorage("role", authData.role || "");
        }
        setSessionStorage("companyName", userInfo.companyName);
        setSessionStorage("organization_id", userInfo.organization_id);
        const profileImageUrl = userInfo.profile_image;
        setSessionStorage(
          "photoUrl",
          profileImageUrl ? await resolveSessionPhotoDisplayUrl(profileImageUrl) : ""
        );
        setSessionStorage("firstName", userInfo.name.split(" ")[0]);
        setSessionStorage("lastName", userInfo.name.split(" ")[1]);
        setSessionStorage(
          "SubscriptionStartDate",
          userInfo?.userSubscription?.subscriptions?.base?.startDate
        );
        setSessionStorage(
          "SubscriptionEndDate",
          userInfo?.userSubscription?.subscriptions?.base?.endDate
        );
        setSessionStorage(
          "SubscriptionType",
          userInfo?.userSubscription?.subscriptions?.base?.planId
        );
        if (userInfo?.userSubscription) {
          setSessionStorage("Paid", true);
        } else {
          setSessionStorage("Paid", false);
        }
        setSessionStorage("currentUrl", pathname);

        let integrationsData = await axios
          .get(
            `${API_SERVICE_BACKEND}/getUserIntegrations/${encodeURIComponent(
              authData.email
            )}`
          )
          .then((res) => res.data)
          .catch((err) => {
            //console.log(err);
            return [];
          });

        updateAuthState({
          isAuthenticated: true,
          user: {
            integrations: {
              hubspot: integrationsData.find(
                (integration) => integration.type === "hubspot"
              ),
              calendly: integrationsData.find(
                (integration) => integration.type === INTEGRATION_TYPE.CALENDLY
              ),
              cronofy: userInfo.extra?.accessToken,
              zoho: integrationsData.find(
                (integration) => integration.type === "zoho"
              ),
              zapier: integrationsData.find(
                (integration) => integration.type === "zapier"
              ),
              pipedrive: integrationsData.find(
                (integration) => integration.type === "pipedrive"
              ),
              salesforce: integrationsData.find(
                (integration) =>
                  integration.type === INTEGRATION_TYPE.SALESFORCE
              ),
            },
            dbRef: userInfo,
            ref: user,
            id: user.uid,
            avatar: user.photoURL || undefined,
            email: authData.email || "UNKNOWN",
            name: user.displayName || "UNKNOWN",
            plan: user?.data?.plan || "GROW", // SCALE | GROW
            organisation: user.data?.organisation || "UNKNOWN",
            whiteLabelUserId: userInfo?.whiteLabelUserId
          },
        });
        const hash = await generateHMAC(
          getSessionStorage("userEmail"),
          process.env.REACT_APP_INTERCOM_HASH_KEY
        );
        if (window.Intercom) {
          window.Intercom("boot", {
            app_id: "qaecvjwi",
            email: getSessionStorage("userEmail"),
            name: getSessionStorage("fullName"),
            user_hash: hash,
          });
        }

        const currentUrl = getSessionStorage("currentUrl");
        //console.log({ currentUrl, ref: document.referrer });

        if (currentUrl === "/auth-main/login/v2") {
          //console.log("locState redirecting to", paths.dashboard.campaigns);
          if (returnTo) {
            //console.log("locState redirecting to", returnTo);
            navigate(returnTo, { state: { prev: currentUrl } });
          } else {
            navigate(paths.authDemo.workspace, {
              state: { prev: currentUrl },
            });
          }
        }
      }
    } catch (ex) {
      //console.log("error", ex);
    }
  };

  const setUserInfo = useCallback(async (email) => {
    const userInfo = await getUserInfo(email);

    const user = {
      email: userInfo.email,
      displayName: userInfo.name,
      photoURL: userInfo.profile_image,
      firstName: userInfo.name.split(" ")[0],
      lastName: userInfo.name.split(" ")[1],
    };

    let integrationsData = await axios
      .get(
        `${API_SERVICE_BACKEND}/getUserIntegrations/${encodeURIComponent(
          email
        )}`
      )
      .then((res) => res.data)
      .catch((err) => {
        //console.log(err);
        return [];
      });

    dispatch({
      type: ActionType.AUTH_STATE_CHANGED,
      payload: {
        isAuthenticated: true,
        user: {
          integrations: {
            hubspot: integrationsData.find(
              (integration) => integration.type === "hubspot"
            ),
            calendly: integrationsData.find(
              (integration) => integration.type === INTEGRATION_TYPE.CALENDLY
            ),
            cronofy: userInfo.extra?.accessToken,
            zoho: integrationsData.find(
              (integration) => integration.type === "zoho"
            ),
            zapier: integrationsData.find(
              (integration) => integration.type === "zapier"
            ),
            pipedrive: integrationsData.find(
              (integration) => integration.type === "pipedrive"
            ),
            salesforce: integrationsData.find(
              (integration) => integration.type === INTEGRATION_TYPE.SALESFORCE
            ),
          },
          dbRef: userInfo,
          ref: user,
          id: user.uid,
          avatar: user.photoURL || undefined,
          email: user.email || "UNKNOWN",
          name: user.displayName || "UNKNOWN",
          plan: user?.data?.plan || "GROW", // SCALE | GROW
          organisation: user.data?.organisation || "UNKNOWN",
          whiteLabelUserId: userInfo?.whiteLabelUserId,
          voiceCallAcknowledgmentAccepted: userInfo?.voiceCallAcknowledgmentAccepted || false,
        },
      },
    });
  }, [dispatch]);

  const handleAuthStateChanged = useCallback(
    async (user) => {
      //console.log("user: :: ::: ", user);
      if (loginInProgress) {
        loginInProgress = false;
        return;
      }
      if (user) {
        // Here you should extract the complete user profile to make it available in your entire app.
        // The auth state only provides basic information.
        const userInfo = await getUserInfo(user.email);

        if (userInfo) {
          const userProfileImage = userInfo.profile_image;
          setSessionStorage(
            "photoUrl",
            userProfileImage ? await resolveSessionPhotoDisplayUrl(userProfileImage) : ""
          );
          setSessionStorage("organization_id", userInfo.organization_id);
          setSessionStorage("userEmail", userInfo.email);
          setSessionStorage("companyName", userInfo.companyName);
          setSessionStorage("fullName", userInfo.name);
          setSessionStorage("firstName", userInfo.name?.split(" ")[0]);
          setSessionStorage("lastName", userInfo.name?.split(" ")[1]);
          setSessionStorage(
            "SubscriptionStartDate",
            userInfo.userSubscription?.subscriptions?.base?.startDate
          );
          setSessionStorage(
            "SubscriptionEndDate",
            userInfo.userSubscription?.subscriptions?.base?.endDate
          );
          setSessionStorage(
            "SubscriptionType",
            userInfo?.userSubscription?.subscriptions?.base?.planId
          );
          setSessionStorage("voiceCallAcknowledgmentAccepted", userInfo?.voiceCallAcknowledgmentAccepted || false);
          if (userInfo?.userSubscription) {
            setSessionStorage("Paid", true);
          } else {
            setSessionStorage("Paid", false);
          }
         setSessionStorage("currentUrl", pathname);
          let integrationsData = await axios
            .get(
              `${API_SERVICE_BACKEND}/getUserIntegrations/${encodeURIComponent(
                user.email
              )}`
            )
            .then((res) => res.data)
            .catch((err) => {
              //console.log(err);
              return [];
            });

          // //console.log(userInfo);
          dispatch({
            type: ActionType.AUTH_STATE_CHANGED,
            payload: {
              isAuthenticated: true,
              user: {
                integrations: {
                  hubspot: integrationsData.find(
                    (integration) => integration.type === "hubspot"
                  ),
                  calendly: integrationsData.find(
                    (integration) => integration.type === INTEGRATION_TYPE.CALENDLY
                  ),
                  cronofy: userInfo.extra?.accessToken,
                  zoho: integrationsData.find(
                    (integration) => integration.type === "zoho"
                  ),
                  zapier: integrationsData.find(
                    (integration) => integration.type === "zapier"
                  ),
                  pipedrive: integrationsData.find(
                    (integration) => integration.type === "pipedrive"
                  ),
                  salesforce: integrationsData.find(
                    (integration) =>
                      integration.type === INTEGRATION_TYPE.SALESFORCE
                  ),
                },
                dbRef: userInfo,
                ref: user,
                id: user.uid,
                avatar: user.photoURL || undefined,
                email: user.email || "UNKNOWN",
                name: user.displayName || "UNKNOWN",
                plan: user?.data?.plan || "GROW", // SCALE | GROW
                organisation: user.data?.organisation || "UNKNOWN",
                whiteLabelUserId: userInfo?.whiteLabelUserId,
                voiceCallAcknowledgmentAccepted: userInfo?.voiceCallAcknowledgmentAccepted || false,
              },
            },
          });
        } else {
          //console.log("auth: user not found in db");
          dispatch({
            type: ActionType.AUTH_STATE_CHANGED,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } else {
        if (!authToken) {
          //console.log("auth: user not found in db");
          dispatch({
            type: ActionType.AUTH_STATE_CHANGED,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        } else {
          magicLinkLogin(authToken);
        }
      }
    },
    [dispatch]
  );
  // useEffect(() => {
  //   // //console.log("check auth====>", auth);
  //   let ownerEma = getSessionStorage("ownerEmail") || null;
  //   if (authToken && ownerEma) {
  //     handleAuthStateChanged();
  //   }
  // }, [])

  useEffect(
    () => {
      // let ownerEma = getSessionStorage("ownerEmail") || null;
      if (authToken) {
        handleAuthStateChanged();
      } else {
        onAuthStateChanged(auth, handleAuthStateChanged);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

const _signInWithEmailAndPassword = useCallback(async (email, password) => {
    loginInProgress = true; // Set flag when login starts
    try {
      // 1. Sign in with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      //console.log("User:", user);

      // 2. Get Firebase ID token
      const idToken = await user.getIdToken();
      //console.log("ID Token:", idToken);

      // 3. Call backend to get JWT and set cookie
      try {
        await fetchAndStoreAuthToken(idToken);
      } catch (err) {
        // Backend failed - sign out from Firebase to prevent login
        await signOut(auth);
        loginInProgress = false;
        throw new Error("Authentication failed. Please try again.");
      }

      // 4. Only proceed if backend succeeded
      await setUserInfo(user.email);

      loginInProgress = false; // Reset flag on success
      return user;

    } catch (error) {
      loginInProgress = false;
      
      if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
        throw new Error("Incorrect password");
      } else if (error.code === "auth/user-not-found") {
        throw new Error("No user found with this email address");
      } else if (error.code === "auth/invalid-email") {
        throw new Error("Invalid email address");
      } else {
        throw error;
      }
    }
  }, [fetchAndStoreAuthToken]);
  // const signInWithGoogle = useCallback(async () => {
  //   const provider = new GoogleAuthProvider();

  //   await signInWithPopup(auth, provider);
  // }, []);
  // const signInWithGoogle = useCallback(async () => {
  //   try {
  //     const provider = new GoogleAuthProvider();
  //     const finalResult = await signInWithPopup(auth, provider);
  //     const { user } = finalResult;
  //     return user;
  //   } catch (ex) {
  //     //console.log({ ex });
  //     return null;
  //   }
  // }, []);

  const signInWithGoogle = useCallback(async () => {
  try {
    const provider = new GoogleAuthProvider();
    const finalResult = await signInWithPopup(auth, provider);
    const { user } = finalResult;

    // Get Firebase ID token
    const idToken = await user.getIdToken();

    try {
      await fetchAndStoreAuthToken(idToken);
    } catch (err) {
      // Backend failed - sign out from Firebase to prevent login
      await signOut(auth);
      toast.error("Authentication failed.");
      return null;
    }

    //await setUserInfo(user.email);

    return user;
  } catch (ex) {
    //console.log({ ex });
    toast.error("Authentication failed.");
    await _signOut();
    return null;
  }
}, [fetchAndStoreAuthToken, _signOut]);

  const _createUserWithEmailAndPassword = useCallback(
    async (email, password) => {
      await createUserWithEmailAndPassword(auth, email, password);
    },
    []
  );

  const _deleteUser = useCallback(
    async (user) => {
      if (user) {
        await deleteUser(user);
      }
    },
    []
  );

  const updateAuthState = useCallback(
    (payload) =>
      dispatch({
        type: "AUTH_STATE_CHANGED",
        payload,
      }),
    [dispatch]
  );

  const contextValue = useMemo(
    () => ({
      ...state,
      updateAuthState,
      issuer: Issuer.Firebase,
      createUserWithEmailAndPassword: _createUserWithEmailAndPassword,
      sendEmailVerification: sendEmailVerification,
      signInWithEmailAndPassword: _signInWithEmailAndPassword,
      signInWithGoogle,
      signOut: _signOut,
      forgotPassword: _forgotPassword,
      setUserInfo: setUserInfo,
      deleteUser: _deleteUser,
    }),
    [
      state,
      updateAuthState,
      _createUserWithEmailAndPassword,
      _signInWithEmailAndPassword,
      signInWithGoogle,
      _signOut,
      _forgotPassword,
      setUserInfo,
      _deleteUser,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const AuthConsumer = AuthContext.Consumer;
